import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  FormControl,
  Grid2,
  Switch,
  Typography,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { useMutation } from "@tanstack/react-query";
import { GKTextField } from "components";
import { useFormik } from "formik";
import { useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { brokerSupport } from "utils/preferences";

interface BrokerValues {
  [key: string]: boolean | string;
  adityaBirlaSupport: boolean;
  pmsSupport: boolean;
  isRaAccess: boolean;
  lmSupport: boolean;
  delayExecution: boolean;
  prodco: string;
  firmco: string;
  iiflSupport: boolean;
  isPayUPayment: boolean;
}

interface Props {
  data: any;
  isFetched: boolean;
  refetch: () => void;
}

const BrokerPreference = (props: Props) => {
  const { data, isFetched, refetch } = props;

  const { id } = useParams();
  const [expanded, setExpanded] = useState<number>(-1);

  const { mutate } = useMutation({
    mutationKey: ["UPDATE_BROKER"],
    mutationFn: (data: any) =>
      axiosInstance.patch(
        `/super_admin/${id}/broker_integration_status/`,
        data
      ),
    onSuccess: (response) => {
      refetch();
      toast.success(response?.data?.message || "Broker Update Successfully");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const formik = useFormik<BrokerValues>({
    enableReinitialize: isFetched,
    initialValues: {
      adityaBirlaSupport:
        data?.data?.data?.brokerIntegration?.adityaBirlaSupport || false,
      pmsSupport: data?.data?.data?.brokerIntegration?.pmsSupport || false,
      isRaAccess: data?.data?.data?.isRaAccess || false,
      lmSupport: data?.data?.data?.brokerIntegration?.lmSupport || false,
      delayExecution:
        data?.data?.data?.brokerIntegration?.delayExecution || false,
      iiflSupport: data?.data?.data?.brokerIntegration?.iiflSupport || false,
      prodco: data?.data?.data?.brokerIntegration?.prodco || "",
      firmco: data?.data?.data?.brokerIntegration?.firmco || "",
      isPayUPayment: data?.data?.data?.isPayUPayment || false,
    },
    onSubmit: (values) => {
      mutate(values);
    },
  });

  return (
    <form id="broker-preferences-form" onSubmit={formik.handleSubmit}>
      {brokerSupport.map((dataItem, index) => (
        <Accordion
          key={index}
          onChange={() => {
            setExpanded(index === expanded ? -1 : index);
          }}
          expanded={index === expanded}
          style={{
            border: `solid 1px var(--neutral-100)`,
            borderRadius: "8px",
            boxShadow: "none",
            background: "none",
          }}
        >
          <AccordionSummary
            expandIcon={<GridExpandMoreIcon />}
            aria-controls={`panel${index + 1}-content`}
            id={`panel${index + 1}-header`}
            sx={{
              "& .MuiAccordionSummary-content": {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              },
            }}
          >
            <Typography
              fontSize={16}
              fontWeight={600}
              textTransform="none"
              color={"var(--primary-900)"}
            >
              {dataItem.mainHeading}
            </Typography>
            {expanded === 0 && (
              <Button
                type="submit"
                variant="contained"
                size="medium"
                form="broker-preferences-form"
                onClick={(e) => e.stopPropagation()}
              >
                Save
              </Button>
            )}
          </AccordionSummary>
          <Divider />
          {dataItem.details.map((data, subIndex) => (
            <AccordionDetails
              key={subIndex}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              {data.key === "lmSupport" ? (
                <>
                  <Switch
                    onChange={(event) => {
                      formik.setFieldValue(data.key, event.target.checked);
                      if (!event.target.checked) {
                        formik.setFieldValue("prodco", "");
                        formik.setFieldValue("firmco", "");
                      }
                    }}
                    checked={formik.values[data.key]}
                  />
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    textTransform="none"
                    color={"var(--black-900)"}
                  >
                    {data.heading}
                  </Typography>
                </>
              ) : (
                <>
                  <Switch
                    onChange={(event) => {
                      formik.setFieldValue(data.key, event.target.checked);
                    }}
                    checked={formik.values[data.key] === true}
                  />
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    textTransform="none"
                    color={"var(--black-900)"}
                  >
                    {data.heading}
                  </Typography>
                </>
              )}
            </AccordionDetails>
          ))}

          <AccordionDetails
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {formik.values.lmSupport && (
              <Grid2 container size={12} spacing={1}>
                <Grid2 size={12}>
                  <AccordionDetails
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      padding: "10px 4px",
                    }}
                  >
                    <Switch
                      checked={formik.values.delayExecution}
                      onChange={(event) => {
                        mutate({ delayExecution: event.target.checked });
                      }}
                    />
                    <Typography
                      fontSize={14}
                      fontWeight={500}
                      textTransform={"none"}
                      color={"var(--black-900)"}
                    >
                      {data?.heading || "Delay sell trades to next day"}
                    </Typography>
                  </AccordionDetails>
                </Grid2>
                <Grid2 size={6}>
                  <FormControl variant="standard" fullWidth>
                    <GKTextField
                      name="prodco"
                      inputLabel="Prodco"
                      required
                      value={formik.values.prodco}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </Grid2>
                <Grid2 size={6}>
                  <FormControl variant="standard" fullWidth>
                    <GKTextField
                      name="firmco"
                      inputLabel="Firmco"
                      required
                      value={formik.values.firmco}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </Grid2>
              </Grid2>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </form>
  );
};

export default BrokerPreference;
